import React, { useState, useEffect } from 'react';
import { Plus, X, Edit2, Trash2, Save, ArrowUp, ArrowDown } from 'lucide-react';
import { portfolioCategoriesAPI } from '../../services/apiService';

const PortfolioCategoriesTab = ({ onCategoriesUpdate }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newCategory, setNewCategory] = useState({
    name_de: '',
    name_ru: '',
    slug: '',
    active: true,
    sort_order: 0
  });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      
      // First try loading from public endpoint which always works
      const publicResponse = await fetch(`${window.location.origin}/api/portfolio-categories`);
      if (publicResponse.ok) {
        const publicData = await publicResponse.json();
        setCategories(publicData.categories || []);
        return;
      }
      
      // Fallback to admin endpoint if public fails
      const response = await portfolioCategoriesAPI.getAll();
      setCategories(response.data.categories || []);
      
    } catch (error) {
      console.error('Error loading categories:', error);
      
      // Final fallback - create default categories
      const defaultCategories = [
        { id: "all", name_de: "Alle", name_ru: "Все", slug: "all", active: true, sort_order: 0 },
        { id: "wedding", name_de: "Hochzeiten", name_ru: "Свадьбы", slug: "wedding", active: true, sort_order: 1 },
        { id: "portrait", name_de: "Portraits", name_ru: "Портреты", slug: "portrait", active: true, sort_order: 2 },
        { id: "event", name_de: "Events", name_ru: "События", slug: "event", active: true, sort_order: 3 },
        { id: "architecture", name_de: "Architektur", name_ru: "Архитектура", slug: "architecture", active: true, sort_order: 4 },
        { id: "restoration", name_de: "Restauration", name_ru: "Реставрация", slug: "restoration", active: true, sort_order: 5 },
        { id: "ai-photoshoot", name_de: "KI-Fotosessions", name_ru: "ИИ-Фотосессии", slug: "ai-photoshoot", active: true, sort_order: 6 }
      ];
      setCategories(defaultCategories);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = async () => {
    try {
      console.log('Creating category with data:', newCategory);
      
      // Generate slug from German name if not provided
      if (!newCategory.slug && newCategory.name_de) {
        newCategory.slug = newCategory.name_de.toLowerCase()
          .replace(/ä/g, 'ae')
          .replace(/ö/g, 'oe')
          .replace(/ü/g, 'ue')
          .replace(/ß/g, 'ss')
          .replace(/[^a-z0-9]/g, '-')
          .replace(/-+/g, '-')
          .replace(/^-|-$/g, '');
      }

      // Set sort order to highest + 1
      const maxOrder = Math.max(...categories.map(c => c.sort_order || 0), 0);
      newCategory.sort_order = maxOrder + 1;

      console.log('Final category data:', newCategory);
      
      const result = await portfolioCategoriesAPI.create(newCategory);
      console.log('Create result:', result);
      
      setShowAddForm(false);
      setNewCategory({
        name_de: '',
        name_ru: '',
        slug: '',
        active: true,
        sort_order: 0
      });
      
      alert('Категория создана успешно!');
      await loadCategories();
      
      // Notify parent component to update categories
      if (onCategoriesUpdate) {
        onCategoriesUpdate();
      }
    } catch (error) {
      console.error('Error creating category:', error);
      console.error('Error details:', error.response);
      alert('Ошибка при создании категории: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleUpdateCategory = async (categoryId, updates) => {
    try {
      await portfolioCategoriesAPI.update(categoryId, updates);
      setEditingId(null);
      await loadCategories();
      
      // Notify parent component to update categories
      if (onCategoriesUpdate) {
        onCategoriesUpdate();
      }
    } catch (error) {
      console.error('Error updating category:', error);
      alert('Ошибка при обновлении категории: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!confirm('Вы уверены, что хотите удалить эту категорию?')) {
      return;
    }

    try {
      await portfolioCategoriesAPI.delete(categoryId);
      await loadCategories();
      
      // Notify parent component to update categories
      if (onCategoriesUpdate) {
        onCategoriesUpdate();
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Ошибка при удалении категории: ' + (error.response?.data?.detail || error.message));
    }
  };

  const moveCategoryUp = async (categoryId) => {
    const categoryIndex = categories.findIndex(c => c.id === categoryId);
    if (categoryIndex > 0) {
      const category = categories[categoryIndex];
      const prevCategory = categories[categoryIndex - 1];
      
      await handleUpdateCategory(category.id, { sort_order: prevCategory.sort_order });
      await handleUpdateCategory(prevCategory.id, { sort_order: category.sort_order });
    }
  };

  const moveCategoryDown = async (categoryId) => {
    const categoryIndex = categories.findIndex(c => c.id === categoryId);
    if (categoryIndex < categories.length - 1) {
      const category = categories[categoryIndex];
      const nextCategory = categories[categoryIndex + 1];
      
      await handleUpdateCategory(category.id, { sort_order: nextCategory.sort_order });
      await handleUpdateCategory(nextCategory.id, { sort_order: category.sort_order });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-white">Категории портфолио</h3>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm transition-colors duration-200"
        >
          <Plus className="h-4 w-4 mr-2" />
          Добавить категорию
        </button>
      </div>

      {/* Add Category Form */}
      {showAddForm && (
        <div className="bg-gray-800 p-4 border border-gray-700 rounded-md">
          <h4 className="text-white font-medium mb-4">Новая категория</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1">🇩🇪 Название (немецкий)</label>
              <input
                type="text"
                value={newCategory.name_de}
                onChange={(e) => setNewCategory({...newCategory, name_de: e.target.value})}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="например: Hochzeiten"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1">🇷🇺 Название (русский)</label>
              <input
                type="text"
                value={newCategory.name_ru}
                onChange={(e) => setNewCategory({...newCategory, name_ru: e.target.value})}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="например: Свадьбы"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1">Slug (URL)</label>
              <input
                type="text"
                value={newCategory.slug}
                onChange={(e) => setNewCategory({...newCategory, slug: e.target.value})}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="например: wedding"
              />
            </div>
          </div>
          <div className="flex justify-end space-x-3 mt-4">
            <button
              onClick={() => setShowAddForm(false)}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-md text-sm"
            >
              Отменить
            </button>
            <button
              onClick={handleAddCategory}
              disabled={!newCategory.name_de || !newCategory.name_ru}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white rounded-md text-sm"
            >
              Создать категорию
            </button>
          </div>
        </div>
      )}

      {/* Categories List */}
      <div className="space-y-3">
        {categories.map((category, index) => (
          <div
            key={category.id}
            className={`bg-gray-800 border border-gray-700 rounded-md p-4 ${
              !category.active ? 'opacity-60' : ''
            }`}
          >
            {editingId === category.id ? (
              <EditCategoryForm
                category={category}
                onSave={(updates) => handleUpdateCategory(category.id, updates)}
                onCancel={() => setEditingId(null)}
              />
            ) : (
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="flex flex-col space-y-1">
                    <button
                      onClick={() => moveCategoryUp(category.id)}
                      disabled={index === 0}
                      className="text-gray-400 hover:text-white disabled:opacity-30"
                    >
                      <ArrowUp className="h-3 w-3" />
                    </button>
                    <button
                      onClick={() => moveCategoryDown(category.id)}
                      disabled={index === categories.length - 1}
                      className="text-gray-400 hover:text-white disabled:opacity-30"
                    >
                      <ArrowDown className="h-3 w-3" />
                    </button>
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <h4 className="text-white font-medium">
                        🇩🇪 {category.name_de} | 🇷🇺 {category.name_ru}
                      </h4>
                      {!category.active && (
                        <span className="px-2 py-1 bg-red-900 text-red-300 text-xs rounded">Неактивно</span>
                      )}
                    </div>
                    <p className="text-gray-400 text-sm">Slug: {category.slug}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setEditingId(category.id)}
                    className="text-blue-400 hover:text-blue-300 p-1"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  {category.id !== 'all' && (
                    <button
                      onClick={() => handleDeleteCategory(category.id)}
                      className="text-red-400 hover:text-red-300 p-1"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const EditCategoryForm = ({ category, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name_de: category.name_de,
    name_ru: category.name_ru,
    slug: category.slug,
    active: category.active
  });

  const handleSubmit = () => {
    onSave(formData);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-300 mb-1">🇩🇪 Название (немецкий)</label>
          <input
            type="text"
            value={formData.name_de}
            onChange={(e) => setFormData({...formData, name_de: e.target.value})}
            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-300 mb-1">🇷🇺 Название (русский)</label>
          <input
            type="text"
            value={formData.name_ru}
            onChange={(e) => setFormData({...formData, name_ru: e.target.value})}
            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-300 mb-1">Slug (URL)</label>
          <input
            type="text"
            value={formData.slug}
            onChange={(e) => setFormData({...formData, slug: e.target.value})}
            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={category.id === 'all'}
          />
        </div>
      </div>
      <div className="flex items-center space-x-4">
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={formData.active}
            onChange={(e) => setFormData({...formData, active: e.target.checked})}
            className="form-checkbox text-blue-600"
            disabled={category.id === 'all'}
          />
          <span className="text-gray-300 text-sm">Активно</span>
        </label>
      </div>
      <div className="flex justify-end space-x-3">
        <button
          onClick={onCancel}
          className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-md text-sm"
        >
          Отменить
        </button>
        <button
          onClick={handleSubmit}
          className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm"
        >
          <Save className="h-4 w-4 mr-2" />
          Сохранить
        </button>
      </div>
    </div>
  );
};

export default PortfolioCategoriesTab;